import { memo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system';
import ArrowTrendingDownIcon from '@flipster/react/icon/generated/ArrowTrendingDownIcon';
import ArrowTrendingUpIcon from '@flipster/react/icon/generated/ArrowTrendingUpIcon';
import { BadgeVariantProps } from '@flipster/styled-system/recipes';
import { OrderMarginType, V2OrderSideType, V2SpotOrderSideType } from '@flipster/utils/types/base';

interface LeverageProps {
  leverage: string | number | undefined;
  marginType?: OrderMarginType;
}

const FormattedMarginTypeLeverage = ({ leverage, marginType }: Required<LeverageProps>) => {
  switch (marginType) {
    case 'Cross':
      return <FormattedMessage id='Trade.r251008.Cross_leverage' values={{ leverage }} />;
    case 'Isolated':
      return <FormattedMessage id='Trade.r251008.Isolated_leverage' values={{ leverage }} />;
    default:
      marginType satisfies never;
      // oxlint-disable-next-line no-console
      console.error(`Unexpected margin type: ${marginType}`);
      return leverage;
  }
};

export const LeverageBadge = memo(({ leverage, marginType }: LeverageProps) => {
  if (!leverage) return null;

  return (
    <Badge color='neutral'>
      {marginType != null ? <FormattedMarginTypeLeverage leverage={leverage} marginType={marginType} /> : leverage}x
    </Badge>
  );
});

interface OrderSideProps {
  orderSide: V2OrderSideType | V2SpotOrderSideType;
  hideText?: boolean;
  hideIcon?: boolean;
}

export const OrderSideBadge = memo(({ orderSide, hideText, hideIcon }: OrderSideProps) => {
  if (!orderSide) return null;

  const meta = {
    Long: {
      color: 'buy',
      trailingIcon: <ArrowTrendingUpIcon />,
    },
    Buy: {
      color: 'buy',
      trailingIcon: undefined,
    },
    Short: {
      color: 'sell',
      trailingIcon: <ArrowTrendingDownIcon />,
    },
    Sell: {
      color: 'sell',
      trailingIcon: undefined,
    },
  } satisfies Record<
    V2OrderSideType | V2SpotOrderSideType,
    { color: BadgeVariantProps['color']; trailingIcon?: React.ReactNode }
  >;

  return (
    <Badge color={meta[orderSide].color} trailingIcon={!hideIcon && meta[orderSide]?.trailingIcon}>
      {!hideText && orderSide}
    </Badge>
  );
});

interface ProductTypeBadgeProps {
  productType: 'perpetual' | 'spot';
}

export const ProductTypeBadge = ({ productType }: ProductTypeBadgeProps) => {
  if (productType === 'perpetual') return <Badge color='neutral'>Perp</Badge>;
  if (productType === 'spot')
    return (
      <Badge color='neutral' type='tint'>
        Spot
      </Badge>
    );
  return null;
};
